/**
 * Centralized Query Key Factory
 *
 * This ensures consistency across the application for React Query keys.
 * Use these keys for useQuery, useMutation, and invalidateQueries.
 *
 * Pattern:
 * - resource: ['ResourceName']
 * - lists: ['ResourceName', 'list', { filters }]
 * - details: ['ResourceName', 'detail', id]
 * - special: ['ResourceName', 'scope', { ...args }]
 */

export const queryKeys = {
	// --- Core FHIR Resources ---

	patient: {
		all: ['Patient'] as const,
		lists: () => [...queryKeys.patient.all, 'list'] as const,
		list: (filters: Record<string, unknown>) => [...queryKeys.patient.lists(), filters] as const,
		details: () => [...queryKeys.patient.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.patient.details(), id] as const,
		// Special Scopes
		addresses: (id: string) => [...queryKeys.patient.detail(id), 'addresses'] as const,
	},

	serviceRequest: {
		all: ['ServiceRequest'] as const,
		lists: () => [...queryKeys.serviceRequest.all, 'list'] as const,
		list: (filters: Record<string, unknown>) =>
			[...queryKeys.serviceRequest.lists(), filters] as const,
		details: () => [...queryKeys.serviceRequest.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.serviceRequest.details(), id] as const,
	},

	observation: {
		all: ['Observation'] as const,
		lists: () => [...queryKeys.observation.all, 'list'] as const,
		list: (filters: Record<string, unknown>) =>
			[...queryKeys.observation.lists(), filters] as const,
		details: () => [...queryKeys.observation.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.observation.details(), id] as const,
		// Special Scopes
		eligibility: (focusId: string) =>
			[...queryKeys.observation.lists(), { code: 'eligibility', focus: focusId }] as const,
	},

	task: {
		all: ['Task'] as const,
		lists: () => [...queryKeys.task.all, 'list'] as const,
		list: (filters: Record<string, unknown>) => [...queryKeys.task.lists(), filters] as const,
		details: () => [...queryKeys.task.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.task.details(), id] as const,
	},

	consent: {
		all: ['Consent'] as const,
		lists: () => [...queryKeys.consent.all, 'list'] as const,
		list: (filters: Record<string, unknown>) => [...queryKeys.consent.lists(), filters] as const,
		details: () => [...queryKeys.consent.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.consent.details(), id] as const,
	},

	relatedPerson: {
		all: ['RelatedPerson'] as const,
		lists: () => [...queryKeys.relatedPerson.all, 'list'] as const,
		list: (filters: Record<string, unknown>) =>
			[...queryKeys.relatedPerson.lists(), filters] as const,
		details: () => [...queryKeys.relatedPerson.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.relatedPerson.details(), id] as const,
		// Special Scope: contacts for a specific patient
		byPatient: (patientId: string) =>
			[...queryKeys.relatedPerson.lists(), { patient: patientId }] as const,
	},

	careTeam: {
		all: ['CareTeam'] as const,
		lists: () => [...queryKeys.careTeam.all, 'list'] as const,
		list: (filters: Record<string, unknown>) => [...queryKeys.careTeam.lists(), filters] as const,
		details: () => [...queryKeys.careTeam.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.careTeam.details(), id] as const,
		// Special Scope: care team for a specific patient/service request
		byPatient: (patientId: string) =>
			[...queryKeys.careTeam.lists(), { patient: patientId }] as const,
		byServiceRequest: (serviceRequestId: string) =>
			[...queryKeys.careTeam.lists(), { serviceRequest: serviceRequestId }] as const,
	},

	episodeOfCare: {
		all: ['EpisodeOfCare'] as const,
		lists: () => [...queryKeys.episodeOfCare.all, 'list'] as const,
		list: (filters: Record<string, unknown>) =>
			[...queryKeys.episodeOfCare.lists(), filters] as const,
		details: () => [...queryKeys.episodeOfCare.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.episodeOfCare.details(), id] as const,
		// Special Scope: episodes for a specific patient
		byPatient: (patientId: string) =>
			[...queryKeys.episodeOfCare.lists(), { patient: patientId }] as const,
		// Special Scope: episodes managed by a specific organization
		byOrganization: (organizationId: string) =>
			[...queryKeys.episodeOfCare.lists(), { organization: organizationId }] as const,
	},

	questionnaire: {
		all: ['Questionnaire'] as const,
		lists: () => [...queryKeys.questionnaire.all, 'list'] as const,
		list: (filters: Record<string, unknown>) =>
			[...queryKeys.questionnaire.lists(), filters] as const,
		details: () => [...queryKeys.questionnaire.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.questionnaire.details(), id] as const,
	},

	// --- Reference Data ---

	codeSystem: {
		all: ['CodeSystem'] as const,
		detail: (url: string) => [...queryKeys.codeSystem.all, url] as const,
	},

	valueSet: {
		all: ['ValueSet'] as const,
		lists: () => [...queryKeys.valueSet.all, 'list'] as const,
		list: (filters: Record<string, unknown>) => [...queryKeys.valueSet.lists(), filters] as const,
		details: () => [...queryKeys.valueSet.all, 'detail'] as const,
		detail: (url: string) => [...queryKeys.valueSet.details(), url] as const,
	},

	// --- Onboarding Checks ---

	onboarding: {
		all: ['onboarding'] as const,
		referralExists: (organizationId: string) =>
			[...queryKeys.onboarding.all, 'referral-exists', organizationId] as const,
		assignedReferralExists: (organizationId: string) =>
			[...queryKeys.onboarding.all, 'assigned-referral-exists', organizationId] as const,
		qualifiedReferralExists: (organizationId: string) =>
			[...queryKeys.onboarding.all, 'qualified-referral-exists', organizationId] as const,
		timeEntryExists: (practitionerId: string) =>
			[...queryKeys.onboarding.all, 'time-entry-exists', practitionerId] as const,
	},
};
